//コンストラクタでUTCにしてtoUnixで日本時間にしている。

import { ColorHexCodes } from "./Color";

//get,setともにUTCがつく関数を使う。
export default class Time {
  public static readonly DayOfWeek = ["日", "月", "火", "水", "木", "金", "土"];
  static readonly TimeDifference = 9 * 60 * 60 * 1000;
  static readonly ADay = 24 * 60 * 60 * 1000;
  static readonly AnHour = 60 * 60 * 1000;
  static readonly AMinute = 60 * 1000;
  private readonly utcDate;

  constructor(epochMillis: number) {
    this.utcDate = new Date(epochMillis + Time.TimeDifference);
  }

  public static readonly Now = () => {
    return new Time(new Date().getTime());
  };

  private readonly Clone = () => {
    return new Time(this.GetTime());
  };

  readonly GetTime = () => {
    return this.utcDate.getTime() - Time.TimeDifference;
  };

  public static readonly FromISO = (isoTimeString: string) => {
    return new Time(new Date(isoTimeString).getTime());
  } 

  readonly StartOfDay = () => {
    const clone = this.Clone();
    clone.utcDate.setUTCHours(0, 0, 0, 0);
    return clone;
  };

  readonly StartOfMonth = () => {
    const clone = this.Clone();
    clone.utcDate.setUTCDate(1);
    clone.utcDate.setUTCHours(0, 0, 0, 0);
    return clone;
  };

  readonly StartOfYear = () => {
    const clone = this.Clone();
    clone.utcDate.setUTCMonth(0, 1);
    clone.utcDate.setUTCHours(0, 0, 0, 0);
    return clone;
  };

  readonly ShiftYear = (diff: number) => {
    const clone = this.Clone();
    clone.utcDate.setUTCFullYear(clone.utcDate.getUTCFullYear() + diff);
    return clone;
  };

  readonly ShiftMonth = (diff: number) => {
    const clone = this.Clone();
    clone.utcDate.setUTCMonth(clone.utcDate.getUTCMonth() + diff);
    return clone;
  };

  readonly ShiftDate = (diff: number) => {
    const clone = this.Clone();
    clone.utcDate.setUTCDate(clone.utcDate.getUTCDate() + diff);
    return clone;
  };

  readonly GetDatesOfMonth = () => {
    const startOfMonth = this.StartOfMonth();
    const datesOfMonth: Time[] = [];
    for (let date = startOfMonth; date.Lt(startOfMonth.ShiftMonth(1)); date = date.ShiftDate(1)) {
      datesOfMonth.push(date);
    }
    return datesOfMonth;
  };

  readonly GetYearDifference = (target: Time) => {
    const date1 = this.utcDate;
    const date2 = target.utcDate;

    // Dateオブジェクトを受け取り、年の差を計算する
    const year1 = date1.getUTCFullYear();
    const year2 = date2.getUTCFullYear()

    // 月や日付を考慮して、実際の年数差を調整
    const month1 = date1.getUTCMonth();
    const month2 = date2.getUTCMonth();
    const day1 = date1.getUTCDate();
    const day2 = date2.getUTCDate();

    // 一旦年数の差を計算
    let yearDifference = year2 - year1;

    // 年の差を正確に調整する
    if (month2 < month1 || (month1 === month2 && day2 < day1)) {
        yearDifference--;
    }

    return yearDifference;
  };

  readonly DateStrings = () => {
    const year = this.utcDate.getUTCFullYear().toString();
    const month = (this.utcDate.getUTCMonth() + 1).toString();
    const month2 = month.padStart(2, "0");
    const date = this.utcDate.getUTCDate().toString();
    const date2 = date.padStart(2, "0");
    const hour = this.utcDate.getUTCHours().toString();
    const hour2 = hour.padStart(2, "0");
    const minute = this.utcDate.getUTCMinutes().toString();
    const minute2 = minute.padStart(2, "0");
    const second = this.utcDate.getUTCSeconds().toString();
    const second2 = second.padStart(2, "0");
    const day = Time.DayOfWeek[this.utcDate.getUTCDay()];
    return { year, month, month2, date, date2, hour, hour2, minute, minute2, second, second2, day };
  };

  readonly Format = (format: "YYYY-MM-dd" | "YYYY/MM/dd" | "YYYY-MM-dd HH:mm" | "YYYY-MM-dd(D) HH:mm:ss" | "YYYY/MM/dd(D) HH:mm:ss" | "YYYY/MM/dd HH:mm:ss" | "YYYY-MM-dd HH:mm:ss") => {
    const ds = this.DateStrings();
    switch (format) {
      case "YYYY-MM-dd":
        return `${ds.year}-${ds.month2}-${ds.date2}`;
      case "YYYY/MM/dd":
        return `${ds.year}/${ds.month2}/${ds.date2}`;
      case "YYYY-MM-dd HH:mm":
        return `${ds.year}-${ds.month2}-${ds.date2} ${ds.hour2}:${ds.minute2}`;
      case "YYYY-MM-dd(D) HH:mm:ss":
        return `${ds.year}-${ds.month2}-${ds.date2}(${ds.day}) ${ds.hour2}:${ds.minute2}:${ds.second2}`;
      case "YYYY/MM/dd(D) HH:mm:ss":
        return `${ds.year}/${ds.month2}/${ds.date2}(${ds.day}) ${ds.hour2}:${ds.minute2}:${ds.second2}`;
      case "YYYY/MM/dd HH:mm:ss":
        return `${ds.year}/${ds.month2}/${ds.date2} ${ds.hour2}:${ds.minute2}:${ds.second2}`;
      case "YYYY-MM-dd HH:mm:ss":
        return `${ds.year}-${ds.month2}-${ds.date2} ${ds.hour2}:${ds.minute2}:${ds.second2}`;
    }
  };

  public static readonly formatMilliSecondsToTime = (milliseconds: number, format: "hh:mm:ss" | "hh時間mm分ss秒") => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const sec = totalSeconds % 60;
    const totalMinutes = Math.floor(totalSeconds / 60);
    const minutes = totalMinutes % 60;
    const hours = Math.floor(totalMinutes / 60);

    const paddedSeconds = String(sec).padStart(2, '0');
    const paddedMinutes = String(minutes).padStart(2, '0');
    const paddedHours = String(hours).padStart(2, '0');

    switch (format) {
      case "hh:mm:ss":
        return `${paddedHours}:${paddedMinutes}:${paddedSeconds}`;
      case "hh時間mm分ss秒":
        let timeString = "";
        if (hours > 0) {
            timeString += `${hours}時`;
        }
        if (minutes > 0 || hours > 0) {
            timeString += `${minutes}分`;
        }
        timeString += `${sec}秒`;
        return timeString;
    }
  }

  readonly GetDay = () => {
    return this.utcDate.getUTCDay();
  };

  readonly GetDayColor = () => {
    const day = this.utcDate.getDay();
    if (day === 0) {
      return `#${ColorHexCodes.SubRed}`;
    }
    if (day === 6) {
      return `#${ColorHexCodes.SubBlue}`;
    }
    return `#${ColorHexCodes.Black}`;
  };

  readonly Lt = (another: Time) => this.GetTime() < another.GetTime();
  readonly Lte = (another: Time) => this.GetTime() <= another.GetTime();
  readonly Gt = (another: Time) => this.GetTime() > another.GetTime();
  readonly Gte = (another: Time) => this.GetTime() >= another.GetTime();
  readonly Eq = (another: Time | undefined) => this.GetTime() === another?.GetTime();
}
